import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, Stack, Typography, Snackbar, Alert } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DumontLogo } from "../../../Generic/Icons";
import { ChatMessage, ChatConversation } from "../../types";
import MessagesList from "./MessagesList";
import ChatInput from "./ChatInput";
import { useChat } from "../../hooks/useChat";
import { useChatActions } from "../../hooks/useChatActions";
import { useConversations } from "../../hooks/useConversations";
import { useAvailableAgents } from "../../hooks/useAvailableAgents";
import FullscreenEmptyChat from "./FullscreenEmptyChat";
import DispatchPrompts from "./DispatchPrompts";
import FullscreenDispatchPrompts from "./FullscreenDispatchPrompts";
import { generateMessageId, generateConversationId } from "../../utils/idGenerator";
import { CHAT_CONFIG } from "../../constants";
import { useAppState } from "../../../../context/StateContext";
import { getAgentTypeFromRoute, getWelcomeMessage, getAgentName } from "../../utils/routeToAgent";

export interface ChatViewProps {
  initialMessages?: ChatMessage[];
  initialConversationId?: string | null;
  isFullscreen?: boolean;
  onHistoryClick?: () => void;
  onBackClick?: () => void;
  showBackButton?: boolean;
  selectedAgentId?: string | null;
}

export default function ChatView({ initialMessages, initialConversationId, isFullscreen, onHistoryClick, onBackClick, showBackButton, selectedAgentId }: ChatViewProps) {
  const { currentUser } = useAppState();
  const userName = currentUser?.name || "User";
  const location = useLocation();
  const { t: translate } = useTranslation();
  const isDispatchPage = location.pathname === "/app/dispatch-operation";
  const { deleteConversation, getConversations, updateConversation, updateConversationTitle, saveConversation, replaceConversationId } = useConversations();
  const { agents: availableAgents } = useAvailableAgents();

  // Detect current section from URL and auto-select agent using route mapping
  // For general routes (Dashboard, Reports, Network, Settings), return null to show default Dumont
  const detectedAgentType = useMemo(() => {
    const agentType = getAgentTypeFromRoute(location.pathname);
    // Only use agentType if detected from route - don't fallback to defaultAgentType for general routes
    return agentType;
  }, [location.pathname]);

  // Get the agentType from the selected agent or from URL detection
  const selectedAgentType = useMemo(() => {
    // If manually selected an agent, use that
    if (selectedAgentId) {
      const agent = availableAgents.find((a) => a.id === selectedAgentId);
      return agent?.type || null;
    }
    // Otherwise use the detected agent type from URL
    return detectedAgentType;
  }, [selectedAgentId, availableAgents, detectedAgentType]);

  // Get the effective agent ID (selected or detected from URL)
  const effectiveAgentId = useMemo(() => {
    if (selectedAgentId) return selectedAgentId;
    
    // Find agent by detected type
    if (detectedAgentType) {
      const agent = availableAgents.find((a) => a.type === detectedAgentType);
      return agent?.id || null;
    }
    return null;
  }, [selectedAgentId, detectedAgentType, availableAgents]);

  const { messages, inputText, setInputText, sendMessage, handleKeyPress, messagesEndRef, setMessages, setCurrentConversationId, resetChat, currentConversationId, handleFileUpload, removeDocument, documentFileName } = useChat({ 
    initialMessages, 
    saveConversation, 
    updateConversation,
    updateConversationTitle,
    replaceConversationId,
    agentId: effectiveAgentId ?? undefined,
  });
  const { copyConversationToClipboard, exportChatAsText } = useChatActions();
  const [isFocused, setIsFocused] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const hasNotifiedBackRef = useRef(false);
  const isMountedRef = useRef(true);
  const dispatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (dispatchTimeoutRef.current) clearTimeout(dispatchTimeoutRef.current);
    };
  }, []);

  // Get welcome message based on detected or selected agent type
  const agentWelcomeMessage = useMemo(() => {
    return getWelcomeMessage(selectedAgentType as any, userName);
  }, [selectedAgentType, userName]);

  // Get agent name for display
  const currentAgentName = useMemo(() => {
    return getAgentName(selectedAgentType as any);
  }, [selectedAgentType]);

  useEffect(() => {
    if (initialMessages) setMessages(initialMessages);
    setCurrentConversationId(initialConversationId ?? null);
  }, [initialMessages, initialConversationId, setMessages, setCurrentConversationId]);

  // Log do agente selecionado ou detectado
  useEffect(() => {
    console.log('🤖 [Dumont] Agent context:', {
      selectedAgentId,
      detectedAgentType,
      effectiveAgentId,
      selectedAgentType,
      currentPath: location.pathname,
    });
  }, [selectedAgentId, detectedAgentType, effectiveAgentId, selectedAgentType, location.pathname]);

  // Notificar apenas uma vez quando houver mensagens na página de dispatch
  useEffect(() => {
    if (onBackClick && messages.length > 0 && isDispatchPage && !hasNotifiedBackRef.current) {
      onBackClick();
      hasNotifiedBackRef.current = true;
    }

    // Reset quando mensagens ficam vazias
    if (messages.length === 0) {
      hasNotifiedBackRef.current = false;
    }
  }, [messages.length, isDispatchPage, onBackClick]);

  const handleCopyConversation = async () => {
    if (messages.length === 0) {
      setSnackbar({ open: true, message: translate("dumontNoMessagesToCopy"), severity: "info" });
      return;
    }
    const success = await copyConversationToClipboard(messages);
    if (success) {
      setSnackbar({ open: true, message: translate("dumontConversationCopied"), severity: "success" });
    } else {
      setSnackbar({ open: true, message: translate("dumontFailedToCopyConversation"), severity: "error" });
    }
  };

  const handleExportChat = () => {
    if (messages.length === 0) {
      setSnackbar({ open: true, message: translate("dumontNoMessagesToExport"), severity: "info" });
      return;
    }
    const conversations = getConversations();
    const currentConv = conversations.find((c) => c.id === currentConversationId);
    const success = exportChatAsText(messages, currentConv?.title);
    if (success) {
      setSnackbar({ open: true, message: translate("dumontChatExportedSuccessfully"), severity: "success" });
    } else {
      setSnackbar({ open: true, message: translate("dumontFailedToExportChat"), severity: "error" });
    }
  };

  const handleClearConversation = () => {
    if (messages.length === 0) {
      setSnackbar({ open: true, message: translate("dumontNoMessagesToClear"), severity: "info" });
      return;
    }
    if (currentConversationId) {
      deleteConversation(currentConversationId);
    }
    resetChat();
    setSnackbar({ open: true, message: translate("dumontConversationCleared"), severity: "success" });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleHistoryClickInternal = () => {
    if (onHistoryClick) {
      onHistoryClick();
    }
  };

  const handleDispatchPrompt = (promptText: string) => {
    // Criar mensagem do usuário
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      text: promptText,
      sender: "dumont",
      timestamp: new Date(),
      showWelcome: true,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Criar resposta da Dumont com dados de dispatch (será substituído por integração real)
    dispatchTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      const dispatchOrders = [
        { orderNumber: "123", date: "Oct 20, 2025", time: "10:00 (AEDT)" },
        { orderNumber: "456", date: "Oct 20, 2025", time: "10:00 (AEDT)" },
        { orderNumber: "789", date: "Oct 20, 2025", time: "10:00 (AEDT)" },
        { orderNumber: "1012", date: "Oct 20, 2025", time: "10:00 (AEDT)" },
      ];

      const dumontResponse: ChatMessage = {
        id: generateMessageId(),
        text: "",
        sender: "dumont",
        timestamp: new Date(),
        dispatchData: dispatchOrders,
      };

      const finalMessages = [...updatedMessages, dumontResponse];
      setMessages(finalMessages);

      if (currentConversationId) {
        updateConversation(currentConversationId, finalMessages);
      } else {
        const conversationId = generateConversationId();
        const title = promptText.length > CHAT_CONFIG.MAX_TITLE_LENGTH
          ? promptText.substring(0, CHAT_CONFIG.MAX_TITLE_LENGTH) + CHAT_CONFIG.TITLE_ELLIPSIS
          : promptText;

        const conversation: ChatConversation = {
          id: conversationId,
          title,
          messages: finalMessages,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        saveConversation(conversation);
        setCurrentConversationId(conversationId);
      }
    }, CHAT_CONFIG.DUMONT_RESPONSE_DELAY_MS);
  };

  if (isFullscreen) {
    return (
      <>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
            {messages.length === 0 ? (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {isDispatchPage ? (
                  <FullscreenDispatchPrompts onPromptClick={(prompt) => handleDispatchPrompt(prompt)} />
                ) : (
                  <FullscreenEmptyChat onPromptClick={(prompt) => setInputText(prompt)} selectedAgentId={selectedAgentId} />
                )}
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box sx={{ width: "100%", maxWidth: "1200px", margin: "0 auto", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <Box sx={{ padding: "0 24px 16px 24px" }}>
                      <Box sx={{ maxWidth: "1152px", margin: "0 auto" }}>
                        <MessagesList messages={messages} endRef={messagesEndRef} isFullscreen={isFullscreen} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <ChatInput
            value={inputText}
            onChange={setInputText}
            onSend={sendMessage}
            onKeyPress={handleKeyPress}
            isFocused={isFocused}
            setFocused={setIsFocused}
            isFullscreen={isFullscreen}
            onCopyConversation={handleCopyConversation}
            onExportChat={handleExportChat}
            onClearConversation={handleClearConversation}
            onHistoryClick={handleHistoryClickInternal}
            onFileUpload={handleFileUpload}
            uploadedFileName={documentFileName || undefined}
            onRemoveFile={removeDocument}
            hasMessages={messages.length > 0}
          />
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // Layout sidebar padrão
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, height: "100%" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {messages.length === 0 && (
            <>
              <Box
                sx={{
                  width: "389px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                  margin: "0 auto",
                  paddingTop: "24px",
                }}
              >
                {/* Logo */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <DumontLogo size={40} />
                  <Typography
                    sx={{
                      fontFamily: "Manrope",
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: "1.4em",
                      color: "#475467",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {translate("dumontHowCanIHelpYou")}
                  </Typography>
                </Box>
              </Box>
              {isDispatchPage && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "28px",
                  }}
                >
                  <DispatchPrompts onPromptClick={(prompt) => handleDispatchPrompt(prompt)} />
                </Box>
              )}
            </>
          )}

          <MessagesList messages={messages} endRef={messagesEndRef} />
        </Box>

        <ChatInput
          value={inputText}
          onChange={setInputText}
          onSend={sendMessage}
          onKeyPress={handleKeyPress}
          isFocused={isFocused}
          setFocused={setIsFocused}
          onCopyConversation={handleCopyConversation}
          onExportChat={handleExportChat}
          onClearConversation={handleClearConversation}
          onHistoryClick={handleHistoryClickInternal}
          onFileUpload={handleFileUpload}
          uploadedFileName={documentFileName || undefined}
          onRemoveFile={removeDocument}
          hasMessages={messages.length > 0}
        />
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}


